/* eslint-disable react-hooks/exhaustive-deps */
/*
  SignalPreviewTable
  ------------------
  Collapsible data preview table for the "Create / Edit Signal" builder.
  - Fetches preview data with the same `getWorkspaceTrendsAgaintQuery` that powers AccountsPage.
  - Provides lightweight column & sort controls (re-uses existing MultiSelectDropdown & SortDropdown).
  - Syncs selected columns and sort back to signalStore so the rest of the builder stays in sync.
*/

import { Accordion, Button, Flex, LoadingOverlay, Text } from "@mantine/core";
import { ITableProps, kaReducer } from "ka-table";
import { hideLoading, loadData, showLoading, updateData, updatePagesCount } from "ka-table/actionCreators";
import { ActionType, SortDirection, SortingMode } from "ka-table/enums";
import { DispatchFunc } from "ka-table/types";
import { isEqual } from "lodash";

import { PencilIcon, SortAsc } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTraitsAndMetrics } from "../../../../../hooks/QueryHooks";
import { getWorkspaceTrendsAgaintQuery } from "../../../../../services/workspace/workspaceEndpoints";
import { ENTITY } from "../../../../../services/workspace/workspaceEndpoints.enums";
import KaTable, { IColumn, ISortBy } from "../../../../../shared/table/KaTable";
import MultiSelectDropdown from "../../../../../shared/ui/dropdown/MultiSelectDropdown";
import SortDropdown from "../../../../../shared/ui/dropdown/SortDropdown";
import useSignalStore from "../../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import parseTableData from "../../../../../utils/parseTable";

const SignalPreviewTable = () => {
  /* ---------------- hooks & stores --------------- */
  const workspace = useWorkspaceStore((s) => s.workspace);
  const {
    signal,
    setSelectedFields,
    setSort,
  } = useSignalStore((s) => ({
    signal: s.signal,
    setSort: s.setSort,
    setSelectedFields: s.setSelectedFields,
  }));

  /* ---------------- traits / metrics ------------- */
  const { data: traitsMetrics, isSuccess } = useTraitsAndMetrics({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  }) as any;

  const traits = useMemo(() => (
    signal.entity_relation === "accounts" ? traitsMetrics?.accountTraits : traitsMetrics?.contactTraits
  ), [signal.entity_relation, traitsMetrics]);

  const metrics = useMemo(() => (
    signal.entity_relation === "accounts" ? traitsMetrics?.accountMetrics : traitsMetrics?.contactMetrics
  ), [signal.entity_relation, traitsMetrics]);

  /* ---------------- dropdown state -------------- */
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  /* ---------------- table state ------------------ */
  const [total, setTotal] = useState(0);
  const [tableProps, changeTableProps] = useState<ITableProps>({
    columns: [],
    columnResizing: true,
    sortingMode: SortingMode.SingleRemote,
    rowKeyField: "randomId",
    height: 250,
    singleAction: loadData(),
    loading: { enabled: true },
  });

  /* ---------------- KA dispatch ------------------ */
  // handles updating global signal store sort object based on ka-table interaction
  const onSortChange = useCallback(({ field, order }: { field: string; order: string }) => {
    setSort({
      field,
      order: order as "asc" | "desc",
      type: { ...traits?.obj, ...metrics?.obj }[field]?.attribute_type === "metric_traits" ? "changeval" : "property",
    });
  }, [traits?.obj, metrics?.obj]);

  // proxy dispatcher that watches for sort actions and routes them to store
  const kaDispatch: DispatchFunc = (action: any) => {
    changeTableProps((prev) => kaReducer(prev, action));
    if (action.type === ActionType.UpdateSortDirection) {
      const sortColumn: any = tableProps.columns.find((c) => c.sortDirection);
      let newDir = SortDirection.Ascend;
      if (sortColumn && sortColumn.key === action.columnKey) {
        newDir = sortColumn.sortDirection === SortDirection.Ascend ? SortDirection.Descend : SortDirection.Ascend;
      }
      onSortChange({ field: action.columnKey, order: newDir === SortDirection.Ascend ? "asc" : "desc" });
    }
  };

  /* ---------------- data fetch ------------------- */
  const fetchData = async (page: number, sortedBy: ISortBy) => {
    if (!workspace?.id) return;
    kaDispatch(showLoading());
    try {
      const response: any = await getWorkspaceTrendsAgaintQuery({
        workspaceId: workspace.id,
        entityType: signal.entity_relation === "accounts" ? ENTITY.ACCOUNTS : ENTITY.USERS,
        signal: undefined,
        query: {
          include_count: true,
          page: page + 1,
          per_page: 5,
          filter: {
            ...signal.filters,
            predicates: signal.filters?.predicates?.map((fil: any) => ({
              ...fil,
              predicates: fil.predicates.filter((p: any) => p.field !== ""),
            })),
          },
          selected_fields: signal.selected_fields?.length > 0 ? signal.selected_fields : [],
          sort: {
            field: sortedBy?.key || (signal.entity_relation === "accounts" ? "accounts.last_seen" : "users.last_seen"),
            order: sortedBy?.direction === SortDirection.Descend ? "desc" : "asc",
            type: sortedBy?.type || ({ ...traits?.obj, ...metrics?.obj }[sortedBy?.key]?.attribute_type === "metric_traits" ? "changeval" : "property"),
          },
          metrics: signal?.metrics?.length > 0 ? signal.metrics : [signal?.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active"],
          time_window: signal.time_window,
          time_zone: workspace.workspace_time_zone,
        },
      });
      if (response?.data) {
        setTotal(response.total_results);
        const list = parseTableData({ data: response.data, memberData: [] });
        kaDispatch(updateData(list));
        kaDispatch(updatePagesCount(Math.ceil(response.total_results / 5)));
      } else {
        setTotal(0);
        kaDispatch(updateData([]));
        kaDispatch(updatePagesCount(1));
      }
    } finally {
      kaDispatch(hideLoading());
    }
  };

  /* ---------------- columns setup ---------------- */
  const buildColumns = useCallback(() => {
    if (!traits?.obj) return [];
    const combined = { ...traits.obj, ...metrics?.obj };
    const cols: IColumn[] = [...signal.selected_fields, ...signal.metrics].reduce((arr: IColumn[], key) => {
      const def = combined[key];
      if (!def || def.exclude_display) return arr;
      const col: any = {
        key,
        title: def.display_name,
        dataType: def.data_type,
        isResizable: true,
        isSortable: true,
        attributeType: def.attribute_type,
      };
      if (signal.sort && key === signal.sort.field) {
        col.sortDirection = signal.sort.order === "asc" ? SortDirection.Ascend : SortDirection.Descend;
        col.sortType = signal.sort.type;
      }
      arr.push(col);
      return arr;
    }, []);
    return cols;
  }, [signal.selected_fields, signal.metrics, traits?.obj, metrics?.obj, signal.sort]);

  /* ---------------- effects ---------------------- */
  useEffect(() => {
    // update columns when selected fields change
    const newCols = buildColumns();
    if (!isEqual(newCols, tableProps.columns)) {
      changeTableProps((prev) => ({ ...prev, columns: newCols }));
    }
  }, [buildColumns]);

  // initial / filter change fetch
  useEffect(() => {
    const sortField = signal.sort?.field || (signal.entity_relation === "accounts" ? "accounts.last_seen" : "users.last_seen");
    const sortOrder = signal.sort?.order || "desc";
    fetchData(0, { key: sortField, direction: sortOrder === "desc" ? SortDirection.Descend : SortDirection.Ascend });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal.filters, signal.selected_fields, signal.metrics, workspace?.id]);

  // refetch when sort changes
  useEffect(() => {
    if (!signal.sort?.field) return;
    fetchData(0, { key: signal.sort.field, direction: signal.sort.order === "desc" ? SortDirection.Descend : SortDirection.Ascend });
    // reset sortDirection flags in columns
    changeTableProps((prev) => ({
      ...prev,
      columns: prev.columns.map((col: any) => ({
        ...col,
        sortDirection: col.key === signal.sort?.field ? (signal.sort.order === "asc" ? SortDirection.Ascend : SortDirection.Descend) : undefined,
        sortType: signal.sort?.type,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal.sort]);

  /* ---------------- UI --------------------------- */
  const filteredTraits = useMemo(() => traits?.list?.filter((t: any) => !t.exclude_display) || [], [traits?.list]);
  const filteredMetrics = useMemo(() => metrics?.list?.filter((m: any) => !m.exclude_display) || [], [metrics?.list]);

  const onSelectColumn = (item: any) => {
    if (!item) return;
    if (item.attribute_type === "metric_traits") {
      const newMetrics = signal.metrics.includes(item.name) ? signal.metrics.filter((n) => n !== item.name) : [...signal.metrics, item.name];
      setSelectedFields([...signal.selected_fields]); // keep columns
      // directly set metrics via store function (not exposed? setMetrics exists but easier?)
      useSignalStore.getState().setMetrics(newMetrics);
    } else {
      const newCols = signal.selected_fields.includes(item.name) ? signal.selected_fields.filter((n) => n !== item.name) : [...signal.selected_fields, item.name];
      setSelectedFields(newCols);
    }
  };

  return (
    <>
      <Flex gap={8} align="center" mt={16} wrap="wrap">
        <MultiSelectDropdown
          selected={[...signal.selected_fields, ...signal.metrics]}
          setSelected={onSelectColumn}
          list={filteredTraits}
          metricsList={filteredMetrics}
          position="bottom-end"
          isOpen={showColumnDropdown}
          setIsOpen={setShowColumnDropdown}
        >
          <Button size="xs" className="px-7 relative" leftSection={<PencilIcon size={14} />} variant="white">Edit Columns</Button>
        </MultiSelectDropdown>
        <SortDropdown
          list={[...filteredTraits, ...filteredMetrics]}
          selectedColumns={[...signal.selected_fields, ...signal.metrics]}
          selectSort={{ 
            value: signal.sort?.field || "", 
            type: signal.sort?.field ? ({ ...traits?.obj, ...metrics?.obj }[signal.sort.field]?.data_type || "") : "", 
            entityType: signal.sort?.field ? ({ ...traits?.obj, ...metrics?.obj }[signal.sort.field]?.attribute_type || "") : "" 
          }}
          selectSortDir={{ order: signal.sort?.order || "desc", type: signal.sort?.type || "property" }}
          isOpen={showSortDropdown}
          setIsOpen={setShowSortDropdown}
          onDone={(field: any, dir: any) => {
            setSort({ field: field.value, order: dir.order, type: dir.type });
          }}
        >
          <div>
            <Button size="xs" className="!px-10 relative" leftSection={<SortAsc size={14} />} variant="white" onClick={() => setShowSortDropdown(true)}>Sort Columns</Button>
          </div>
        </SortDropdown>
      </Flex>
      <Accordion variant="contained" className="mt-6 bg-gray-100">
        <Accordion.Item value="preview">
          <Accordion.Control>
            <Flex gap={8} align="center" wrap="wrap">
              {total > 0 ? <Text size="xs" className="italic " c="gray.7">{total} {signal.entity_relation === "accounts" ? "Accounts" : "Users"} currently meet these conditions </Text> : <Text size="xs" className="italic " c="gray.7">No data found for current filters</Text>}
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            <LoadingOverlay visible={!isSuccess} />
            <KaTable
              tableProps={tableProps}
              changeTableProps={changeTableProps}
              kaDispatch={kaDispatch}
              totalData={total}
              noDataTitle="No data"
              noDataMessage="No rows for current filters"
              rowKeyField="randomId"
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default SignalPreviewTable;
